import React, { useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const popIn = keyframes`
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

const toneStyles = {
  neutral: css`
    background: #ffffff;
    color: #1a2e40;
    border-color: #ced9e5;

    &:hover:not(:disabled) {
      background: #f6f9fc;
      border-color: #b9c8d8;
    }
  `,
  primary: css`
    background: #4caf50;
    color: #ffffff;
    border-color: #3f9543;

    &:hover:not(:disabled) {
      background: #449b48;
      border-color: #38813b;
    }
  `,
  danger: css`
    background: #e53935;
    color: #ffffff;
    border-color: #d32f2f;

    &:hover:not(:disabled) {
      background: #d9322f;
      border-color: #bf2b29;
    }
  `,
};

// Styled Components
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.58);
  display: flex;
  align-items: ${({ $allowBodyOverflow }) => ($allowBodyOverflow ? 'flex-start' : 'center')};
  justify-content: center;
  z-index: 2100;
  padding:
    max(1rem, env(safe-area-inset-top, 0px))
    max(1rem, env(safe-area-inset-right, 0px))
    max(1rem, env(safe-area-inset-bottom, 0px))
    max(1rem, env(safe-area-inset-left, 0px));
  min-height: 100dvh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;
  animation: ${fadeIn} 0.3s ease forwards;

  @media (max-width: 1024px) {
    align-items: ${({ $allowBodyOverflow }) => ($allowBodyOverflow ? 'flex-start' : 'center')};
    padding:
      max(0.9rem, env(safe-area-inset-top, 0px))
      max(0.5rem, env(safe-area-inset-right, 0px))
      max(0.9rem, env(safe-area-inset-bottom, 0px))
      max(0.5rem, env(safe-area-inset-left, 0px));
  }
`;

const Content = styled.div`
  background: white;
  border-radius: 14px;
  padding: 0.95rem;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  max-width: ${({ $size }) => {
    if ($size === 'sm') return '420px';
    if ($size === 'lg') return '860px';
    return '560px';
  }};
  width: 100%;
  max-height: ${({ $allowBodyOverflow }) => ($allowBodyOverflow ? 'none' : 'min(92dvh, 900px)')};
  border: 1px solid rgba(209, 220, 231, 0.9);
  animation: ${popIn} 0.25s ease forwards;

  &.modal-wide {
    max-width: min(1100px, 96vw);
  }

  &.modal-full {
    max-width: min(1240px, 98vw);
  }

  @media (max-width: 1024px) {
    max-width: 100%;
    max-height: ${({ $allowBodyOverflow }) => (
      $allowBodyOverflow
        ? 'none'
        : 'calc(100dvh - max(0.5rem, env(safe-area-inset-top, 0px)) - max(0.5rem, env(safe-area-inset-bottom, 0px)))'
    )};
    border-radius: 16px 16px 12px 12px;
    padding: 0.78rem;
  }
`;

const Header = styled.div`
  flex-shrink: 0;
  padding: 0.2rem 0.18rem 0.68rem;
  border-bottom: ${({ $showDivider }) => ($showDivider ? '1px solid #e7edf3' : '0')};
`;

const Title = styled.div`
  font-family: 'Roboto', sans-serif;
  font-size: 1.03rem;
  font-weight: 700;
  color: #1A2E40;
  margin: 0;
  line-height: 1.3;
  word-wrap: break-word;
`;

const Message = styled.p`
  font-family: 'Roboto', sans-serif;
  color: #4f6071;
  margin: 0.55rem 0 0 0;
  line-height: 1.4;
  font-size: 0.9rem;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 0.7rem;
  padding: 0.72rem 0.18rem 0.05rem;
  border-top: ${({ $showDivider }) => ($showDivider ? '1px solid #e7edf3' : '0')};
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 1;

  @media (max-width: 640px) {
    flex-direction: row;
    justify-content: stretch;
    align-items: stretch;
  }
`;

const Button = styled.button`
  min-height: 44px;
  padding: 0.625rem 1rem;
  border: 1px solid transparent;
  border-radius: 6px;
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 15px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #1a2e40;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
  touch-action: manipulation;
  ${({ $tone }) => toneStyles[$tone] || toneStyles.neutral}

  &:hover:not(:disabled) {
    box-shadow: 0 4px 6px rgba(0,0,0,0.07);
  }

  ${(props) =>
    props.disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      &:hover {
        transform: none;
        box-shadow: 0 1px 3px rgba(0,0,0,0.12);
      }
    `}

  @media (max-width: 640px) {
    width: 100%;
    min-height: 46px;
  }
`;

const ChildrenWrapper = styled.div`
  min-height: 0;
  margin-top: 0.72rem;
  max-height: ${({ $size, $allowBodyOverflow }) => (
    $allowBodyOverflow ? 'none' : ($size === 'lg' ? 'min(68dvh, 640px)' : 'min(56dvh, 460px)')
  )};
  overflow-y: ${({ $allowBodyOverflow }) => ($allowBodyOverflow ? 'visible' : 'auto')};
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;

  @media (max-width: 1024px) {
    max-height: ${({ $size, $allowBodyOverflow }) => (
      $allowBodyOverflow ? 'none' : ($size === 'lg' ? 'min(70dvh, 760px)' : 'min(58dvh, 540px)')
    )};
  }
`;

// Modal Component
const Modal = ({
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirmer',
  cancelText = 'Annuler',
  singleButton = false,
  confirmDisabled = false,
  confirmVariant = 'primary',
  size = 'md',
  className,
  children
}) => {
  const allowBodyOverflow = typeof className === 'string' && className.includes('modal-goals-form');
  const isTouchDevice = typeof window !== 'undefined'
    && Boolean(window.matchMedia?.('(hover: none), (pointer: coarse)').matches);

  const handleOverlayClick = e => {
    if (e.target === e.currentTarget && onCancel) {
      onCancel();
    }
  };

  const handleConfirm = () => {
    if (!confirmDisabled && onConfirm) {
      onConfirm();
    }
  };

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const previousViewportContent = viewportMeta?.getAttribute('content')
      || 'width=device-width, initial-scale=1';
    const previousOverflow = document.body.style.overflow;

    const normalizedViewportTokens = previousViewportContent
      .split(',')
      .map((token) => token.trim())
      .filter(Boolean)
      .filter((token) => !/^maximum-scale=/i.test(token) && !/^user-scalable=/i.test(token));

    document.body.style.overflow = 'hidden';
    if (viewportMeta && isTouchDevice) {
      viewportMeta.setAttribute(
        'content',
        `${normalizedViewportTokens.join(', ')}, maximum-scale=1, user-scalable=no`
      );
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape' && onCancel) {
        onCancel();
      }
    };
    const preventGesture = (event) => {
      event.preventDefault();
    };
    const preventPinchTouch = (event) => {
      if (event.touches && event.touches.length > 1) {
        event.preventDefault();
      }
    };
    const preventCtrlWheelZoom = (event) => {
      if (event.ctrlKey) {
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('gesturestart', preventGesture, { passive: false });
    document.addEventListener('gesturechange', preventGesture, { passive: false });
    document.addEventListener('gestureend', preventGesture, { passive: false });
    document.addEventListener('touchmove', preventPinchTouch, { passive: false });
    document.addEventListener('wheel', preventCtrlWheelZoom, { passive: false });

    return () => {
      document.body.style.overflow = previousOverflow;
      if (viewportMeta && isTouchDevice) {
        viewportMeta.setAttribute('content', previousViewportContent);
      }
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('gesturestart', preventGesture);
      document.removeEventListener('gesturechange', preventGesture);
      document.removeEventListener('gestureend', preventGesture);
      document.removeEventListener('touchmove', preventPinchTouch);
      document.removeEventListener('wheel', preventCtrlWheelZoom);
    };
  }, [isTouchDevice, onCancel]);

  const hasHeader = Boolean(title || message);
  const hasBody = Boolean(children);
  const showHeaderDivider = hasBody;
  const showActionsDivider = hasHeader || hasBody;

  return (
    <Overlay $allowBodyOverflow={allowBodyOverflow} onClick={handleOverlayClick}>
      <Content $size={size} $allowBodyOverflow={allowBodyOverflow} className={className}>
        {hasHeader && (
          <Header $showDivider={showHeaderDivider}>
            {title && <Title>{title}</Title>}
            {message && <Message>{message}</Message>}
          </Header>
        )}
        {children && (
          <ChildrenWrapper $size={size} $allowBodyOverflow={allowBodyOverflow}>
            {children}
          </ChildrenWrapper>
        )}
        <Actions $showDivider={showActionsDivider}>
          {singleButton ? (
            <Button $tone="primary" onClick={onCancel}>OK</Button>
          ) : (
            <>
              <Button $tone="neutral" onClick={onCancel}>{cancelText}</Button>
              <Button
                $tone={confirmVariant === 'danger' ? 'danger' : 'primary'}
                onClick={handleConfirm}
                disabled={confirmDisabled}
              >
                {confirmText}
              </Button>
            </>
          )}
        </Actions>
      </Content>
    </Overlay>
  );
};

export default Modal;
